"use client";

import { useState, useCallback } from 'react';
import { X, UploadCloud, ChevronRight, Package, Trash2, FileCheck2, AlertTriangle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { assetService } from '@/services/asset-service';
import { createStagedFile, fileTypeToAssetTypeMap } from './types';
import type { StagedFile, StagedFileType } from './types';
import { AssetUploadDropzone } from './asset-upload-dropzone.tsx';
import { UploadButtons } from './upload-buttons.tsx';

// PROPS FOR THE REUSABLE COMPONENT
type Props = {
    onClose: () => void;
    onComplete: () => void;
};

// Helper function to validate if a dependency is allowed for a given main file type
const isValidDependency = (mainFileType: StagedFileType, dependencyFile: StagedFile): boolean => {
    // For glTF files, allow image files and binary (.bin) files
    if (mainFileType === 'gltf') {
        const allowedDependencyTypes: StagedFileType[] = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'svg', 'bin'];
        return allowedDependencyTypes.includes(dependencyFile.type);
    }

    // For now, other file types don't have specific dependency restrictions
    return true;
};

// THE MAIN REUSABLE COMPONENT (without modal wrapper)
export function AssetUploadContent({ onClose, onComplete }: Props) {
    // State now only holds top-level files. Dependencies are nested within.
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);

    // Handles adding new files from the dropzone
    const handleAddFiles = useCallback((files: FileList | null) => {
        if (!files) return;
        const newFiles: StagedFile[] = Array.from(files).map(file => createStagedFile(file));
        setStagedFiles(prev => [...prev, ...newFiles]);
    }, []);

    // Removes a top-level file and its nested dependencies
    const removeFile = useCallback((id: string) => {
        setStagedFiles(prev => prev.filter(sf => sf.id !== id));
    }, []);

    // Moves a dependency back to the top-level list
    const promoteDependency = useCallback((mainFileId: string, dependencyKey: string) => {
        setStagedFiles(prev => {
            const mainFile = prev.find(f => f.id === mainFileId);
            const dependency = mainFile?.dependencies?.[dependencyKey];
            if (!mainFile || !dependency) return prev;

            const nextState = prev.map(sf => {
                if (sf.id === mainFileId) {
                    const { [dependencyKey]: _, ...restDeps } = sf.dependencies || {};
                    return { ...sf, dependencies: restDeps };
                }
                return sf;
            });
            return [...nextState, dependency];
        });
    }, []);

    // Handles dropping files onto a card to create a dependency
    const handleDropOnCard = useCallback((mainFileId: string, dt: DataTransfer) => {
        const draggedFileId = dt.getData('text/plain');

        // CASE 1: Internal drag of an existing staged file
        if (draggedFileId && draggedFileId !== mainFileId) {
            const fileToMove = stagedFiles.find(f => f.id === draggedFileId);
            const targetFile = stagedFiles.find(f => f.id === mainFileId);
            if (!fileToMove || !targetFile) return;

            // Validate dependency
            if (!isValidDependency(targetFile.type, fileToMove)) {
                toast.error(`'${fileToMove.file.name}' is not a valid dependency for '${targetFile.file.name}' (type: ${targetFile.type}).`);
                return;
            }

            setStagedFiles(prev => {
                const remainingFiles = prev.filter(f => f.id !== draggedFileId);
                return remainingFiles.map(sf => {
                    if (sf.id === mainFileId) {
                        const dependencyKey = fileToMove.file.name;
                        return {
                            ...sf,
                            dependencies: { ...(sf.dependencies || {}), [dependencyKey]: fileToMove },
                        };
                    }
                    return sf;
                });
            });
            toast.info(`'${fileToMove.file.name}' is now a dependency of '${targetFile.file.name}'.`);

        // CASE 2: Dropping files from the OS
        } else if (dt.files?.length > 0) {
            const newDependencies = Array.from(dt.files).map(file => createStagedFile(file));

            // Filter valid dependencies for the target file
            const validDependencies: StagedFile[] = [];
            const invalidDependencies = [];

            const targetFile = stagedFiles.find(f => f.id === mainFileId);
            if (targetFile) {
                for (const dep of newDependencies) {
                    if (isValidDependency(targetFile.type, dep)) {
                        validDependencies.push(dep);
                    } else {
                        invalidDependencies.push(dep);
                    }
                }
            } else {
                // If target file not found, consider all as valid (shouldn't happen)
                validDependencies.push(...newDependencies);
            }

            // Add valid dependencies
            if (validDependencies.length > 0) {
                setStagedFiles(prev =>
                    prev.map(sf => {
                        if (sf.id === mainFileId) {
                            const addedDeps = validDependencies.reduce((acc, dep) => {
                                acc[dep.file.name] = dep;
                                return acc;
                            }, {} as Record<string, StagedFile>);
                            return { ...sf, dependencies: { ...(sf.dependencies || {}), ...addedDeps } };
                        }
                        return sf;
                    })
                );
            }

            // Show toast for valid dependencies
            if (validDependencies.length > 0) {
                toast.info(`${validDependencies.length} valid file(s) added as dependencies.`);
            }

            // Show toast for invalid dependencies if any
            if (invalidDependencies.length > 0) {
                toast.error(`${invalidDependencies.length} file(s) rejected - not valid dependencies for this asset type.`);
            }
        }
    }, [stagedFiles]);

    const toggleCollapse = useCallback((fileId: string) => {
        setStagedFiles(prev =>
            prev.map(sf => (sf.id === fileId ? { ...sf, isCollapsed: !sf.isCollapsed } : sf))
        );
    }, []);

    const handleConfirmUpload = async () => {
        const filesToUpload = stagedFiles.filter(f => f.status === 'ready');
        if (filesToUpload.length === 0) {
            toast.warning("No new files to upload.");
            return;
        }

        // Set status to 'creating' for all relevant files and their dependencies
        setStagedFiles(prev => prev.map(sf => (sf.status === 'ready' ? {
            ...sf,
            status: 'creating',
            dependencies: Object.fromEntries(Object.entries(sf.dependencies || {}).map(([key, dep]) => [key, { ...dep, status: 'creating' }]))
        } : sf)));

        // Track successful uploads and errors separately
        const successfulUploads: string[] = [];
        const failedUploads: Array<{ id: string; error: string }> = [];

        const uploadPromises = filesToUpload.map(async (mainFile) => {
            try {
                let newAsset;

                // Handle files with dependencies differently from files without dependencies
                const assetType = fileTypeToAssetTypeMap[mainFile.type];

                if (Object.keys(mainFile.dependencies || {}).length > 0) {
                    // Upload main file with dependencies
                    newAsset = await assetService.createAssetWithDependencies(
                        mainFile.file,
                        mainFile.name,
                        assetType,
                        Object.fromEntries(Object.entries(mainFile.dependencies!).map(([key, dep]) => [key, dep.file]))
                    );
                } else {
                    // Upload main file without dependencies
                    newAsset = await assetService.createFileAsset(
                        mainFile.file,
                        mainFile.name,
                        assetType
                    );
                }

                // Update UI to show completion status
                setStagedFiles(prev => prev.map(sf => sf.id === mainFile.id ? {
                    ...sf,
                    status: 'complete',
                    finalAssetUuid: newAsset.uuid,
                    dependencies: Object.fromEntries(Object.entries(sf.dependencies || {}).map(([key, dep]) => [key, { ...dep, status: 'complete' }]))
                } : sf));

                successfulUploads.push(mainFile.id);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Upload failed.';

                // Update main file status to error and also update dependencies to error state
                setStagedFiles(prev => prev.map(sf => sf.id === mainFile.id ? {
                    ...sf,
                    status: 'error',
                    error: errorMessage,
                    dependencies: Object.fromEntries(Object.entries(sf.dependencies || {}).map(([key, dep]) => [key, { ...dep, status: 'error' }]))
                } : sf));

                failedUploads.push({ id: mainFile.id, error: errorMessage });
            }
        });

        await Promise.allSettled(uploadPromises);

        // Show appropriate feedback based on results
        if (successfulUploads.length > 0 && failedUploads.length === 0) {
            toast.success(`Successfully uploaded ${successfulUploads.length} asset(s)!`);
        } else if (failedUploads.length > 0 && successfulUploads.length > 0) {
            toast.warning(`Uploaded ${successfulUploads.length} asset(s), but ${failedUploads.length} failed.`);
        } else if (failedUploads.length > 0 && successfulUploads.length === 0) {
            toast.error(`Failed to upload ${failedUploads.length} asset(s).`);
        } else {
            toast.info("Upload process completed.");
        }

        // Only call onComplete if there are no errors
        if (failedUploads.length === 0) {
            onComplete();
        }
    };

    const handleReset = () => setStagedFiles([]);
    const readyFilesCount = stagedFiles.filter(f => f.status === 'ready').length;

    return (
        <div className="space-y-4 flex flex-col flex-1 min-h-0">
            {stagedFiles.length === 0 ? (
                <AssetUploadDropzone onFileDrop={handleAddFiles} />
            ) : (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto p-1">
                    {stagedFiles.map(sf => (
                        <MainFileCard
                            key={sf.id}
                            stagedFile={sf}
                            onToggleCollapse={toggleCollapse}
                            onRemoveFile={removeFile}
                            onPromoteDependency={promoteDependency}
                            onDropOnCard={handleDropOnCard}
                        />
                    ))}
                </div>
            )}

            {stagedFiles.length > 0 && (
                <div className="border-t pt-4">
                    <UploadButtons
                        onConfirmUpload={handleConfirmUpload}
                        onReset={handleReset}
                        readyFilesCount={readyFilesCount}
                        batchStatus={stagedFiles.some(f => f.status === 'creating') ? 'processing' : 'idle'}
                    />
                </div>
            )}

            <Alert>
                <AlertDescription>
                    🔒 <strong>Personal Asset:</strong> Only you can see this asset. You can share it with your organization later.
                </AlertDescription>
            </Alert>
        </div>
    );
}

// SUBCOMPONENT FOR MAIN FILE CARD
interface MainFileCardProps {
    stagedFile: StagedFile;
    onToggleCollapse: (fileId: string) => void;
    onRemoveFile: (id: string) => void;
    onPromoteDependency: (mainFileId: string, dependencyKey: string) => void;
    onDropOnCard: (mainFileId: string, dt: DataTransfer) => void;
}

const MainFileCard: React.FC<MainFileCardProps> = ({ stagedFile, onToggleCollapse, onRemoveFile, onPromoteDependency, onDropOnCard }) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const dependencies = Object.entries(stagedFile.dependencies || {});
    const isProcessing = stagedFile.status === 'creating' || stagedFile.status === 'complete';

    // Count dependencies by status
    const dependencyStatusCounts = dependencies.reduce((counts, [, dep]) => {
        counts[dep.status] = (counts[dep.status] || 0) + 1;
        return counts;
    }, {} as Record<string, number>);

    return (
        <Card
            className={`transition-all ${isDragOver ? 'border-primary ring-2 ring-primary' : ''} ${
                stagedFile.status === 'error' ? 'border-destructive' :
                stagedFile.status === 'creating' ? 'border-blue-500' :
                stagedFile.status === 'complete' ? 'border-green-500' : ''
            }`}
            draggable={stagedFile.status === 'ready'}
            onDragStart={(e) => {
                if (stagedFile.status !== 'ready') return e.preventDefault();
                e.dataTransfer.setData('text/plain', stagedFile.id);
                e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); onDropOnCard(stagedFile.id, e.dataTransfer); }}
        >
            <CardContent className="p-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0" onClick={() => onToggleCollapse(stagedFile.id)}>
                        <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${stagedFile.isCollapsed ? '' : 'rotate-90'}`} />
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            stagedFile.status === 'error' ? 'bg-destructive text-destructive-foreground' :
                            stagedFile.status === 'creating' ? 'bg-blue-500 text-white' :
                            stagedFile.status === 'complete' ? 'bg-green-500 text-white' :
                            'text-primary'
                        }`}>
                            {stagedFile.status === 'error' ? <X className="w-4 h-4" /> :
                             stagedFile.status === 'creating' ? <UploadCloud className="w-4 h-4 animate-pulse" /> :
                             stagedFile.status === 'complete' ? <FileCheck2 className="w-4 h-4" /> :
                             <Package className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="font-medium truncate">{stagedFile.name}</p>
                                {stagedFile.status === 'error' && stagedFile.error && (
                                    <span className="text-xs bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded" title={stagedFile.error}>
                                        Error
                                    </span>
                                )}
                                {stagedFile.status === 'creating' && (
                                    <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">
                                        Uploading...
                                    </span>
                                )}
                                {stagedFile.status === 'complete' && (
                                    <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">
                                        Complete
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                                {stagedFile.type}
                                {stagedFile.type === 'gltf' && ' (supports image/.bin deps)'}
                                - {(stagedFile.file.size / 1024).toFixed(2)} KB
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {dependencies.length > 0 && (
                            <div className="flex gap-1">
                                {dependencyStatusCounts.creating && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full">{dependencyStatusCounts.creating} ↻</span>
                                )}
                                {dependencyStatusCounts.complete && (
                                    <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">{dependencyStatusCounts.complete} ✓</span>
                                )}
                                {dependencyStatusCounts.error && (
                                    <span className="text-xs bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full">{dependencyStatusCounts.error} ✗</span>
                                )}
                            </div>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => onRemoveFile(stagedFile.id)} className="h-8 w-8" disabled={isProcessing}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {!stagedFile.isCollapsed && (
                    <div className="border-t mt-3 pt-3 space-y-3">
                        {/* You can place the StagedFileItem here to edit the name, etc. */}
                        {dependencies.length > 0 && (
                            <div>
                                <h4 className="font-medium flex items-center gap-2 text-sm mb-2 text-muted-foreground">
                                    <UploadCloud className="w-4 h-4" /> Dependencies
                                </h4>
                                <div className="pl-5 border-l-2 ml-2 space-y-2">
                                    {dependencies.map(([key, dep]) => (
                                        <DependencyItem
                                            key={dep.id}
                                            dependency={dep}
                                            onPromote={() => onPromoteDependency(stagedFile.id, key)}
                                            isProcessing={isProcessing}
                                            parentType={stagedFile.type}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                         {dependencies.length === 0 && (
                            <div className="text-center text-sm text-muted-foreground py-4 border-2 border-dashed rounded-md">
                                Drag files here to add as dependencies
                                {stagedFile.type === 'gltf' && ' (supports image/.bin files)'}
                            </div>
                         )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// SUBCOMPONENT FOR DEPENDENCY ITEMS
interface DependencyItemProps {
    dependency: StagedFile;
    onPromote: () => void;
    isProcessing: boolean;
    parentType?: StagedFileType;
}

const DependencyItem: React.FC<DependencyItemProps> = ({ dependency, onPromote, isProcessing, parentType }) => {
    const isValid = parentType ? isValidDependency(parentType, dependency) : true;

    return (
        <div className={`flex items-center justify-between p-2 rounded ${isValid ? 'bg-muted/50' : 'bg-destructive/20'}`}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    dependency.status === 'error' ? 'bg-destructive text-destructive-foreground' :
                    dependency.status === 'creating' ? 'bg-blue-500 text-white' :
                    dependency.status === 'complete' ? 'bg-green-500 text-white' :
                    isValid ? 'text-muted-foreground' : 'bg-destructive text-destructive-foreground'
                }`}>
                    {dependency.status === 'error' ? <X className="w-2.5 h-2.5" /> :
                     dependency.status === 'creating' ? <UploadCloud className="w-2.5 h-2.5 animate-pulse" /> :
                     dependency.status === 'complete' ? <FileCheck2 className="w-2.5 h-2.5" /> :
                     isValid ? <Package className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                        <p className={`font-medium text-sm truncate ${!isValid ? 'text-destructive' : ''}`} title={dependency.file.name}>{dependency.file.name}</p>
                        {dependency.status === 'error' && dependency.error && (
                            <span className="text-xs bg-destructive text-destructive-foreground px-1 rounded" title={dependency.error}>
                                Err
                            </span>
                        )}
                        {dependency.status === 'creating' && (
                            <span className="text-xs bg-blue-500 text-white px-1 rounded">
                                ↻
                            </span>
                        )}
                        {dependency.status === 'complete' && (
                            <span className="text-xs bg-green-500 text-white px-1 rounded">
                                ✓
                            </span>
                        )}
                        {!isValid && (
                            <span className="text-xs bg-destructive text-destructive-foreground px-1 rounded">
                                Invalid
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onPromote} className="h-7 w-7" disabled={isProcessing} title="Remove from group">
                <X className="w-3.5 h-3.5" />
            </Button>
        </div>
    );
};
